import { STATE_NAMES } from './states';
import type { AwardInput, CertificationInput, JobInput, Positioned, Validation, ValidationReason, YearMonth } from './types';

/**
 * Entry rules for the profile (R2, R6, R7), ordering, completeness, and the name rule R19
 * compares with. Pure: the current year arrives as an argument because core cannot read the clock.
 */

/** Awards earlier than this are almost certainly typos. */
export const MIN_AWARD_YEAR = 1950;
/** "A short note" (R7). */
export const MAX_NOTE_LENGTH = 280;
/** A generous cap for every free-text field, so one entry cannot fill a row. */
export const MAX_TEXT_LENGTH = 200;
/** A Florida certificate number, with room to spare. */
export const MAX_LICENSE_LENGTH = 40;

/** 50 states and DC. Any of these may hold a certification; only TX and FL are verified (R4). */
export const US_STATES: readonly string[] = Object.freeze(Object.keys(STATE_NAMES));

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

const fail = <F extends string>(field: F, reason: ValidationReason) => ({ ok: false as const, field, reason });

/** Trimmed text, or the failure for a missing or oversized field. */
function text<F extends string>(field: F, value: string | null | undefined) {
  const v = (value ?? '').trim();
  if (!v) return fail(field, 'required');
  if (v.length > MAX_TEXT_LENGTH) return fail(field, 'too_long');
  return { ok: true as const, value: v };
}

/** Optional text: blank becomes null. */
function optional(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v ? v : null;
}

export function isYearMonth(value: string): value is YearMonth {
  return MONTH.test(value);
}

type JobField = keyof JobInput;

/** R6. End may equal start (a one-month role); null end is the current role. */
export function validateJob(input: JobInput): Validation<JobInput, JobField> {
  const role = text('role', input.role);
  if (!role.ok) return role;
  const school = text('schoolOrDistrict', input.schoolOrDistrict);
  if (!school.ok) return school;
  const start = (input.startMonth ?? '').trim();
  if (!start) return fail('startMonth', 'required');
  if (!isYearMonth(start)) return fail('startMonth', 'invalid');
  const end = optional(input.endMonth);
  if (end !== null) {
    if (!isYearMonth(end)) return fail('endMonth', 'invalid');
    // Zero-padded 'YYYY-MM' compares correctly as a string.
    if (end < start) return fail('endMonth', 'before_start');
  }
  return { ok: true, value: { role: role.value, schoolOrDistrict: school.value, startMonth: start, endMonth: end } };
}

type AwardField = keyof AwardInput;

/** R7. `currentYear` comes from the shell's clock. */
export function validateAward(input: AwardInput, currentYear: number): Validation<AwardInput, AwardField> {
  const name = text('name', input.name);
  if (!name.ok) return name;
  const issuer = text('issuer', input.issuer);
  if (!issuer.ok) return issuer;
  if (!Number.isInteger(input.year) || input.year < MIN_AWARD_YEAR || input.year > currentYear) {
    return fail('year', 'out_of_range');
  }
  const note = optional(input.note);
  if (note !== null && note.length > MAX_NOTE_LENGTH) return fail('note', 'too_long');
  return { ok: true, value: { name: name.value, issuer: issuer.value, year: input.year, note } };
}

type CertificationField = keyof CertificationInput;

/** R2. Any US state; whether it can be verified is `isVerifiableState`'s question (R4). */
export function validateCertification(input: CertificationInput): Validation<CertificationInput, CertificationField> {
  const rawState = (input.state ?? '').trim().toUpperCase();
  if (!rawState) return fail('state', 'required');
  if (!US_STATES.includes(rawState)) return fail('state', 'invalid');
  const area = text('area', input.area);
  if (!area.ok) return area;
  const name = text('nameOnCertificate', input.nameOnCertificate);
  if (!name.ok) return name;
  const license = optional(input.licenseNumber);
  if (license !== null && license.length > MAX_LICENSE_LENGTH) return fail('licenseNumber', 'too_long');
  return { ok: true, value: { state: rawState, area: area.value, nameOnCertificate: name.value, licenseNumber: license } };
}

function byPosition<T extends Positioned>(entries: readonly T[]): T[] {
  return [...entries].sort((a, b) => a.position - b.position);
}

function renumber<T extends Positioned>(entries: readonly T[]): T[] {
  return entries.map((e, position) => ({ ...e, position }));
}

/**
 * Places entries in `order` (a list of their ids) with dense positions from 0. Returns null
 * when `order` is not exactly a permutation of the entries' ids, so a stale client cannot drop
 * or duplicate an entry.
 */
export function reorder<T extends Positioned>(entries: readonly T[], order: readonly string[]): T[] | null {
  if (order.length !== entries.length || new Set(order).size !== order.length) return null;
  const byId = new Map(entries.map((e) => [e.id, e]));
  const out: T[] = [];
  for (const id of order) {
    const e = byId.get(id);
    if (!e) return null;
    out.push(e);
  }
  return renumber(out);
}

function move<T extends Positioned>(entries: readonly T[], id: string, delta: -1 | 1): T[] {
  const list = byPosition(entries);
  const i = list.findIndex((e) => e.id === id);
  const j = i + delta;
  const a = list[i];
  const b = list[j];
  if (i >= 0 && a && b) [list[i], list[j]] = [b, a];
  return renumber(list);
}

/** One place earlier; the first entry stays put. */
export const moveUp = <T extends Positioned>(entries: readonly T[], id: string) => move(entries, id, -1);
/** One place later; the last entry stays put. */
export const moveDown = <T extends Positioned>(entries: readonly T[], id: string) => move(entries, id, 1);

/** The v1 success signal: at least one job and one award. Certifications do not count. */
export function isComplete({ jobs, awards }: { jobs: readonly unknown[]; awards: readonly unknown[] }): boolean {
  return jobs.length > 0 && awards.length > 0;
}

/**
 * The form names are compared in (R19, KTD6, KTD7): trimmed, inner whitespace collapsed to one
 * space, diacritics stripped, case-folded. Idempotent.
 */
export function normalizeName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Enough for any real career; a ceiling so one owner cannot fill a table. */
export const MAX_ENTRIES_PER_SECTION = 50;

/**
 * R1. The title, the one identity field the owner edits here: the name and photo come from
 * their 240 account (`core/identity`). Absent keeps the saved title; blank is allowed. A
 * non-string value is `invalid` rather than coerced.
 */
export function validateTitle(current: string, edit: Record<string, unknown>): Validation<{ title: string }, 'title'> {
  const title = 'title' in edit ? edit.title : current;
  if (title !== null && typeof title !== 'string') return fail('title', 'invalid');
  const t = (title ?? '').trim();
  if (t.length > MAX_TEXT_LENGTH) return fail('title', 'too_long');
  return { ok: true, value: { title: t } };
}

/**
 * The display name a new profile starts with (F1): the token's name, else the email's local
 * part read as words (`sam.miller` → `Sam Miller`), else blank for the owner to fill in.
 */
export function initialDisplayName({ name, email }: { name?: string | null; email?: string | null }): string {
  const fromToken = (name ?? '').trim().replace(/\s+/g, ' ');
  if (fromToken) return fromToken.slice(0, MAX_TEXT_LENGTH);
  const local = (email ?? '').split('@')[0] ?? '';
  return local
    .split(/[._+-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .slice(0, MAX_TEXT_LENGTH);
}
